import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Space, Tag, Tabs, Card, Progress, Grid, Drawer } from 'antd';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckCircleFilled,
  FormOutlined,
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  HomeOutlined,
  ClockCircleOutlined,
  CompassOutlined,
  BookOutlined,
  FileTextOutlined,
  EditOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { lessonApi } from '../../../api/lesson.api.js';
import { courseApi } from '../../../api/course.api.js';
import { learningPathApi } from '../../../api/learning-path.api.js';
import { VideoPlayer } from '../components/VideoPlayer.jsx';
import { MarkdownContent } from '../components/MarkdownContent.jsx';
import { LessonSidebar } from '../components/LessonSidebar.jsx';
import { AttachmentsBox } from '../components/AttachmentsBox.jsx';
import { TableOfContents } from '../components/TableOfContents.jsx';
import { AssignmentBox } from '../components/AssignmentBox.jsx';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner.jsx';
import { ErrorState } from '../../../components/common/ErrorState.jsx';
import { LessonPaywall } from '../components/LessonPaywall.jsx';
import { ModuleGateNotice } from '../components/ModuleGateNotice.jsx';
import { PASS_RULE_TEXT } from '../utils/moduleGate.js';
import { parseLessonSections } from '../utils/sectionParser.js';
import '../styles/lesson-player.css';

export function LessonPlayerPage() {
  const { courseId, lessonId, pathId: urlPathId } = useParams();
  const [searchParams] = useSearchParams();
  const queryPathId = searchParams.get('pathId');
  const activePathId = urlPathId || queryPathId;

  const navigate = useNavigate();

  const [lessonData, setLessonData] = useState(null);
  const [courseDetails, setCourseDetails] = useState(null);
  const [learningPath, setLearningPath] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);
  const [activeTabKey, setActiveTabKey] = useState('main');
  const [viewMode, setViewMode] = useState('section'); // 'section' (xem từng phần) | 'all' (cuộn dài)
  const [activePartNo, setActivePartNo] = useState(1); // 1, 2, ..., 'summary', 'intro'
  // Dưới mốc lg (992px) giáo trình mở dạng ngăn kéo, không chiếm cột bên phải
  const screens = Grid.useBreakpoint();
  const isCompact = !screens.lg;
  const [syllabusDrawerOpen, setSyllabusDrawerOpen] = useState(false);

  const fetchLessonAndContext = useCallback(async () => {
    setLoading(true);
    setError(null);
    setErrorCode(null);
    setErrorDetails(null);
    try {
      const promises = [lessonApi.getLessonDetails(lessonId)];

      // Nếu có courseId
      if (courseId) {
        promises.push(courseApi.getCourseDetails(courseId).catch(() => ({ data: null })));
      } else {
        promises.push(Promise.resolve({ data: null }));
      }

      // Nếu có activePathId (Đang học theo Lộ Trình)
      if (activePathId) {
        promises.push(learningPathApi.getPathDetails(activePathId).catch(() => ({ data: null })));
      } else {
        promises.push(Promise.resolve({ data: null }));
      }

      const [lessonRes, courseRes, pathRes] = await Promise.all(promises);

      setLessonData(lessonRes.data);
      setIsCompleted(lessonRes.data?.isCompleted || false);
      setCourseDetails(courseRes.data);
      setLearningPath(pathRes.data);
      setActiveTabKey('main'); // Reset về bài học chính khi đổi bài
      setActivePartNo(1); // Reset về phần 1 khi chuyển bài

      // Nếu không có courseId trên URL nhưng lesson trả về courseId -> tự tải courseDetails
      if (!courseRes.data && lessonRes.data?.lesson?.courseId) {
        const cRes = await courseApi.getCourseDetails(lessonRes.data.lesson.courseId).catch(() => ({ data: null }));
        setCourseDetails(cRes.data);
      }
    } catch (err) {
      setError(err.message || 'Không thể tải nội dung bài học');
      setErrorCode(err.code || null);
      setErrorDetails(err.details || null);
    } finally {
      setLoading(false);
    }
  }, [courseId, lessonId, activePathId]);

  useEffect(() => {
    fetchLessonAndContext();
    window.scrollTo(0, 0);
    setSyllabusDrawerOpen(false);
  }, [fetchLessonAndContext]);

  // Phân tích bài học thành các phần trọng tâm
  const parsedSections = useMemo(() => {
    if (!lessonData?.lesson?.contentMarkdown) {
      return { intro: '', sections: [], summary: '', isMultiPart: false };
    }
    return parseLessonSections(lessonData.lesson.contentMarkdown, lessonData.lesson.parts || []);
  }, [lessonData?.lesson?.contentMarkdown, lessonData?.lesson?.parts]);

  if (loading) {
    return <LoadingSpinner minHeight="100vh" tip="Đang chuẩn bị bài giảng..." />;
  }

  if (errorCode === 'MODULE_LOCKED' || errorCode === 'MODULE_UNDER_CONSTRUCTION') {
    return <ModuleGateNotice code={errorCode} message={error} details={errorDetails} />;
  }

  if (errorCode === 'SUBSCRIPTION_REQUIRED') {
    return <LessonPaywall message={error} />;
  }

  if (error || !lessonData?.lesson) {
    return <ErrorState title="Lỗi tải bài học" subTitle={error} onRetry={fetchLessonAndContext} />;
  }

  const { lesson, nextLesson: courseNextLesson, prevLesson: coursePrevLesson } = lessonData;
  const { course, syllabus = [], quiz: courseQuiz, progress } = courseDetails || {};
  const activeQuiz = lesson.quiz || courseQuiz;
  const attachments = Array.isArray(lesson.attachments) ? lesson.attachments : [];

  // Tính toán Bài tiếp theo & Bài trước theo ngữ cảnh Lộ trình hoặc Khóa học
  let nextLesson = courseNextLesson;
  let prevLesson = coursePrevLesson;

  if (learningPath && learningPath.items && learningPath.items.length > 0) {
    const items = learningPath.items;
    const currentIndex = items.findIndex((it) => it.lesson?.id === lesson.id);
    if (currentIndex !== -1) {
      prevLesson = currentIndex > 0 ? items[currentIndex - 1].lesson : null;
      nextLesson = currentIndex < items.length - 1 ? items[currentIndex + 1].lesson : null;
    }
  }

  const handleNavigateLesson = (targetLesson) => {
    if (!targetLesson) return;
    if (activePathId) {
      navigate(`/learn/paths/${activePathId}/lessons/${targetLesson.id}`);
    } else {
      navigate(`/learn/${targetLesson.courseId || courseId}/lessons/${targetLesson.id}`);
    }
  };

  const handleDownloadCsv = (item) => {
    if (item.csvContent) {
      const blob = new Blob(['\uFEFF' + item.csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${item.code || 'tai-lieu'}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  // Xác định nội dung phần cần hiển thị
  let currentSection = null;
  let currentContent = lesson.contentMarkdown;

  if (viewMode === 'section' && parsedSections.isMultiPart) {
    if (activePartNo === 'summary') {
      currentContent = parsedSections.summary || lesson.contentMarkdown;
    } else if (activePartNo === 'intro') {
      currentContent = parsedSections.intro || lesson.contentMarkdown;
    } else {
      currentSection =
        parsedSections.sections.find((s) => s.no === activePartNo) || parsedSections.sections[0];
      currentContent = currentSection ? currentSection.content : lesson.contentMarkdown;
    }
  }

  const currentIdx = currentSection
    ? parsedSections.sections.findIndex((s) => s.no === currentSection.no)
    : -1;
  const prevPart = currentIdx > 0 ? parsedSections.sections[currentIdx - 1] : null;
  const nextPart =
    currentIdx >= 0 && currentIdx < parsedSections.sections.length - 1
      ? parsedSections.sections[currentIdx + 1]
      : null;

  const scrollToContentTop = () => {
    const el = document.getElementById('lesson-content-area');
    if (el) {
      const offset = 70;
      const topPos = el.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: Math.max(0, topPos), behavior: 'smooth' });
    }
  };

  // Tạo các Tab chuyên sâu (Bài học chính, các Tài liệu chuyên sâu con, Bài tập thực hành)
  const tabItems = [
    {
      key: 'main',
      label: (
        <span>
          <BookOutlined /> Bài Giảng Cốt Lõi
        </span>
      ),
      children: (
        <div>
          {/* Table of Contents (Mục lục 5-7 phần hỗ trợ chọn từng phần) */}
          {(lesson.parts?.length > 0 || parsedSections.sections.length > 0) && (
            <TableOfContents
              parts={lesson.parts?.length > 0 ? lesson.parts : parsedSections.sections}
              activePartNo={activePartNo}
              viewMode={viewMode}
              onChangeViewMode={(mode) => setViewMode(mode)}
              onSelectPart={(no) => {
                setActivePartNo(no);
                scrollToContentTop();
              }}
              hasSummary={Boolean(parsedSections.summary)}
              onSelectSummary={() => {
                setActivePartNo('summary');
                scrollToContentTop();
              }}
            />
          )}

          {/* Attachments Box (Khối tài liệu tải về & chuyển tab nhanh) */}
          {attachments.length > 0 && (
            <AttachmentsBox
              attachments={attachments}
              onSelectDocument={(att) => {
                setActiveTabKey(att.code || att.name);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />
          )}

          {/* Markdown Lesson Content Area */}
          <div
            id="lesson-content-area"
            className="lesson-content-box"
            style={{
              background: '#ffffff',
              borderRadius: 12,
              padding: '32px 28px',
              border: '1px solid #e2e8f0',
              marginBottom: 24,
              boxShadow: '0 1px 4px rgba(0,0,0,0.03)',
            }}
          >
            {/* Header info khi ở chế độ xem từng phần */}
            {viewMode === 'section' && parsedSections.isMultiPart && (
              <div
                className="lesson-section-head"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 12,
                  padding: '14px 18px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 10,
                  marginBottom: 24,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <Tag
                    color="#005537"
                    style={{
                      fontWeight: 800,
                      fontSize: 13,
                      padding: '3px 10px',
                      borderRadius: 6,
                    }}
                  >
                    {activePartNo === 'summary'
                      ? '🎯 TỔNG KẾT BÀI HỌC'
                      : `PHẦN ${currentSection?.no || 1} / ${parsedSections.sections.length}`}
                  </Tag>
                  <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                    {activePartNo === 'summary'
                      ? 'Tóm Tắt 1 Trang & Ghi Nhớ Thực Chiến'
                      : currentSection?.title || lesson.title}
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                    Tiến độ bài:{' '}
                    {activePartNo === 'summary'
                      ? '100%'
                      : `${Math.round(((currentIdx >= 0 ? currentIdx + 1 : 1) / parsedSections.sections.length) * 100)}%`}
                  </span>
                  <Progress
                    percent={
                      activePartNo === 'summary'
                        ? 100
                        : Math.round(((currentIdx >= 0 ? currentIdx + 1 : 1) / parsedSections.sections.length) * 100)
                    }
                    size="small"
                    style={{ width: 90, margin: 0 }}
                    strokeColor="#005537"
                    showInfo={false}
                  />
                </div>
              </div>
            )}

            {/* Khi ở Phần 1 và có đoạn Intro mở đầu, hiển thị Card Mục tiêu bài học gọn gàng phía trên */}
            {viewMode === 'section' &&
              parsedSections.isMultiPart &&
              activePartNo === 1 &&
              parsedSections.intro && (
                <div
                  className="lesson-intro-box"
                  style={{
                    marginBottom: 28,
                    padding: '18px 22px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: 10,
                  }}
                >
                  <MarkdownContent content={parsedSections.intro} />
                </div>
              )}

            {/* Nội dung markdown của phần đang chọn */}
            <MarkdownContent content={currentContent} />

            {/* Thanh điều hướng chuyển tiếp giữa các phần (Bottom Section Navigator) */}
            {viewMode === 'section' && parsedSections.isMultiPart && (
              <div
                className="lesson-part-nav"
                style={{
                  marginTop: 36,
                  paddingTop: 22,
                  borderTop: '1.5px solid #f1f5f9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 12,
                }}
              >
                {prevPart ? (
                  <Button
                    size="large"
                    icon={<ArrowLeftOutlined />}
                    onClick={() => {
                      setActivePartNo(prevPart.no);
                      scrollToContentTop();
                    }}
                    style={{
                      borderRadius: 8,
                      fontWeight: 600,
                      color: '#005537',
                      borderColor: '#cbd5e1',
                      background: '#ffffff',
                      height: 42,
                    }}
                  >
                    Phần {prevPart.no}:{' '}
                    {prevPart.title?.length > 25
                      ? `${prevPart.title.substring(0, 25)}...`
                      : prevPart.title}
                  </Button>
                ) : (
                  <div style={{ fontSize: 13, color: '#94a3b8', fontStyle: 'italic' }}>
                    🚩 Đang ở phần mở đầu bài học
                  </div>
                )}

                {nextPart ? (
                  <Button
                    type="primary"
                    size="large"
                    onClick={() => {
                      setActivePartNo(nextPart.no);
                      scrollToContentTop();
                    }}
                    style={{
                      borderRadius: 8,
                      fontWeight: 700,
                      background: '#005537',
                      borderColor: '#005537',
                      height: 42,
                      padding: '0 22px',
                      color: '#ffffff',
                    }}
                  >
                    Sang Phần {nextPart.no}:{' '}
                    {nextPart.title?.length > 25
                      ? `${nextPart.title.substring(0, 25)}...`
                      : nextPart.title}{' '}
                    <ArrowRightOutlined />
                  </Button>
                ) : parsedSections.summary && activePartNo !== 'summary' ? (
                  <Button
                    type="primary"
                    size="large"
                    onClick={() => {
                      setActivePartNo('summary');
                      scrollToContentTop();
                    }}
                    style={{
                      borderRadius: 8,
                      fontWeight: 700,
                      background: '#005537',
                      borderColor: '#005537',
                      height: 42,
                      padding: '0 22px',
                      color: '#ffffff',
                    }}
                  >
                    🎯 Xem Tổng Kết Bài Học 1 Trang <ArrowRightOutlined />
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    size="large"
                    onClick={() => {
                      setActiveTabKey('assignment');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    style={{
                      borderRadius: 8,
                      fontWeight: 700,
                      background: '#005537',
                      borderColor: '#005537',
                      height: 42,
                      padding: '0 22px',
                      color: '#ffffff',
                    }}
                  >
                    ✍️ Mở Tab Bài Tập Thực Hành <ArrowRightOutlined />
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      ),
    },
    ...attachments.map((att, idx) => ({
      key: att.code || String(idx),
      label: (
        <span>
          <FileTextOutlined /> {att.code || `Tài liệu ${idx + 1}`} ({att.format || 'DOC'})
        </span>
      ),
      children: (
        <div style={{ marginBottom: 24 }}>
          {/* Header of document reading */}
          <Card
            className="lesson-doc-head"
            style={{
              marginBottom: 16,
              borderRadius: 10,
              border: '1px solid #bae6fd',
              background: '#f0f9ff',
            }}
            styles={{ body: { padding: '16px 20px' } }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <Tag color="cyan" style={{ fontWeight: 700 }}>
                    {att.code}
                  </Tag>
                  <Tag color="blue">{att.format}</Tag>
                  <span style={{ fontSize: 12, color: '#0369a1', fontWeight: 600 }}>
                    Tài liệu bài học chuyên sâu
                  </span>
                </div>
                <h2 style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', margin: 0 }}>
                  {att.name}
                </h2>
              </div>

              <Space wrap>
                {att.csvContent && (
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownloadCsv(att)}
                    style={{ background: '#16a34a', borderColor: '#16a34a' }}
                  >
                    Tải File Excel / CSV Chạy Được
                  </Button>
                )}
                <Button onClick={() => setActiveTabKey('main')}>
                  Quay lại bài giảng cốt lõi
                </Button>
              </Space>
            </div>
          </Card>

          {/* Document Markdown Content */}
          <div
            className="lesson-content-box"
            style={{
              background: '#ffffff',
              borderRadius: 10,
              padding: '32px 28px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
            }}
          >
            {att.contentMarkdown ? (
              <MarkdownContent content={att.contentMarkdown} />
            ) : (
              <div style={{ padding: 24, textAlign: 'center', color: '#64748b' }}>
                Nội dung tài liệu đang được ban biên tập đóng gói bản phát hành.
              </div>
            )}
          </div>
        </div>
      ),
    })),
    ...(lesson.assignment
      ? [
          {
            key: 'assignment',
            label: (
              <span>
                <EditOutlined /> Bài Tập Thực Hành
              </span>
            ),
            children: (
              <div style={{ marginBottom: 24 }}>
                <Card
                  className="lesson-doc-head"
                  style={{
                    marginBottom: 16,
                    borderRadius: 10,
                    border: '1px solid #bbf7d0',
                    background: '#f0fdf4',
                  }}
                  styles={{ body: { padding: '16px 20px' } }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 12,
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <Tag color="green" style={{ fontWeight: 700 }}>
                          THỰC HÀNH TƯƠNG TÁC
                        </Tag>
                        <span style={{ fontSize: 12, color: '#15803d', fontWeight: 600 }}>
                          Bài tập áp dụng tại quầy
                        </span>
                      </div>
                      <h2 style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', margin: 0 }}>
                        {lesson.assignment.title || `Bài tập thực hành — ${lesson.title}`}
                      </h2>
                    </div>

                    <Button
                      onClick={() => {
                        setActiveTabKey('main');
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      style={{ fontWeight: 600 }}
                    >
                      ⬅️ Quay lại bài giảng cốt lõi
                    </Button>
                  </div>
                </Card>

                <AssignmentBox lessonId={lesson.id} assignment={lesson.assignment} lessonTitle={lesson.title} />
              </div>
            ),
          },
        ]
      : []),
  ];

  const sidebarContent = (
    <LessonSidebar
      course={course}
      learningPath={learningPath}
      syllabus={syllabus}
      currentLessonId={lesson.id}
      completedLessonIds={progress?.completedLessonIds || []}
      progress={progress}
      quiz={activeQuiz}
    />
  );

  const sidebarVisible = isCompact ? syllabusDrawerOpen : showSidebar;
  const handleToggleSidebar = () => {
    if (isCompact) {
      setSyllabusDrawerOpen(!syllabusDrawerOpen);
    } else {
      setShowSidebar(!showSidebar);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#f8fafc' }}>
      {/* Top Learning Navigation Bar */}
      <header
        className="lesson-header"
        style={{
          height: 56,
          background: '#0f172a',
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 16px',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        }}
      >
        <div className="lesson-header-left" style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
          {learningPath ? (
            <Button
              type="text"
              icon={<CompassOutlined style={{ color: '#c084fc' }} />}
              onClick={() => navigate('/courses?tab=paths')}
              style={{ color: '#fff', flexShrink: 0 }}
              title="Về danh sách lộ trình"
            >
              <span className="lesson-header-btn-label">Về danh sách lộ trình</span>
            </Button>
          ) : (
            <Button
              type="text"
              icon={<ArrowLeftOutlined style={{ color: '#fff' }} />}
              onClick={() => navigate(`/courses/${course?.slug || courseId}`)}
              style={{ color: '#fff', flexShrink: 0 }}
              title="Về chuyên đề"
            >
              <span className="lesson-header-btn-label">Về chuyên đề</span>
            </Button>
          )}

          <span className="lesson-header-sep" style={{ color: '#475569' }}>|</span>

          <span className="lesson-header-title" style={{ fontSize: 14, fontWeight: 600, color: '#e2e8f0', maxWidth: 520, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {learningPath ? `[${learningPath.code}] ` : ''}
            {lesson.code && !lesson.title.startsWith(lesson.code) ? `[${lesson.code}] ` : ''}
            {lesson.title}
          </span>
        </div>

        <Space size={isCompact ? 4 : 12} style={{ flexShrink: 0, marginLeft: 8 }}>
          {activeQuiz && (
            <Button
              type="primary"
              size="small"
              icon={<FormOutlined />}
              onClick={() => navigate(`/quiz/${activeQuiz.id}`)}
              style={{ background: '#f59e0b', borderColor: '#f59e0b', fontWeight: 600 }}
            >
              {isCompact ? 'Quiz' : `Làm Quiz (${activeQuiz.totalQuestions || 10} câu)`}
            </Button>
          )}

          <Button
            type="text"
            icon={sidebarVisible ? <MenuFoldOutlined style={{ color: '#fff' }} /> : <MenuUnfoldOutlined style={{ color: '#fff' }} />}
            onClick={handleToggleSidebar}
            style={{ color: '#fff' }}
          >
            {isCompact ? 'Giáo trình' : showSidebar ? 'Ẩn giáo trình' : 'Hiện giáo trình'}
          </Button>
        </Space>
      </header>

      {/* Main Content & Sidebar Layout */}
      <div style={{ flex: 1, display: 'flex', position: 'relative' }}>
        {/* Lesson Content Area */}
        <main className="lesson-main" style={{ flex: 1, minWidth: 0, padding: '24px 24px 80px' }}>
          <div style={{ maxWidth: 1160, margin: '0 auto' }}>
            {/* Lesson Title Header */}
            <div style={{ marginBottom: 20 }}>
              <div className="lesson-meta-tags" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                {learningPath && (
                  <Tag color="purple" style={{ fontWeight: 700 }}>
                    {learningPath.code} — {learningPath.title}
                  </Tag>
                )}
                {lesson.code && <Tag color="blue" style={{ fontWeight: 700, fontSize: 13 }}>Module {lesson.code}</Tag>}
                <Tag color="green">Miễn Phí</Tag>
                {lesson.estimatedMinutes && (
                  <Tag icon={<ClockCircleOutlined />} color="default">
                    {lesson.estimatedMinutes} phút học
                  </Tag>
                )}
                {isCompleted ? (
                  <Tag color="success" icon={<CheckCircleFilled />}>
                    Đã hoàn thành bài học (Đạt Quiz)
                  </Tag>
                ) : (
                  <Tag color="orange" icon={<FormOutlined />}>
                    Điều kiện hoàn thành: Vượt qua bài kiểm tra trắc nghiệm
                  </Tag>
                )}
              </div>

              <h1 className="lesson-title" style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', lineHeight: 1.35, marginBottom: 6 }}>
                {lesson.title}
              </h1>
            </div>

            {/* Video Player (nếu có videoUrl) */}
            {lesson.lessonType === 'VIDEO' && lesson.videoUrl && (
              <VideoPlayer videoUrl={lesson.videoUrl} title={lesson.title} />
            )}

            {/* Sub-lessons and Document tabs */}
            <Tabs
              activeKey={activeTabKey}
              onChange={setActiveTabKey}
              items={tabItems}
              type="card"
              style={{ marginTop: 12 }}
            />

            {/* Bottom Actions Navigation */}
            <div
              className="lesson-bottom-nav"
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 16,
                padding: '18px 24px',
                background: '#ffffff',
                borderRadius: 10,
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                marginTop: 20,
              }}
            >
              <div>
                {prevLesson ? (
                  <Button
                    icon={<ArrowLeftOutlined />}
                    onClick={() => handleNavigateLesson(prevLesson)}
                  >
                    Bài trước ({prevLesson.code || ''})
                  </Button>
                ) : (
                  <Button icon={<HomeOutlined />} onClick={() => navigate(activePathId ? '/courses?tab=paths' : `/courses/${courseId}`)}>
                    {activePathId ? 'Danh sách lộ trình' : 'Tổng quan chuyên đề'}
                  </Button>
                )}
              </div>

              {/* Status & Quiz trigger (Hệ thống tự động xử lý khi pass Quiz) */}
              <div style={{ textAlign: 'center' }}>
                {isCompleted ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#16a34a', fontWeight: 600, fontSize: 14 }}>
                    <CheckCircleFilled style={{ fontSize: 18, color: '#16a34a' }} />
                    <span>Bài học đã được ghi nhận HOÀN THÀNH</span>
                  </div>
                ) : activeQuiz ? (
                  <Button
                    type="primary"
                    icon={<FormOutlined />}
                    onClick={() => navigate(`/quiz/${activeQuiz.id}`)}
                    style={{ background: '#f59e0b', borderColor: '#f59e0b', fontWeight: 700, height: 40 }}
                  >
                    Làm Bài Kiểm Tra Để Mở Module Tiếp Theo ({PASS_RULE_TEXT})
                  </Button>
                ) : (
                  <Tag color="blue">Đang theo học bài giảng</Tag>
                )}
              </div>

              <div>
                {nextLesson ? (
                  <Button
                    type="primary"
                    onClick={() => handleNavigateLesson(nextLesson)}
                    style={{ background: '#0f172a', borderColor: '#0f172a' }}
                  >
                    Bài tiếp theo ({nextLesson.code || ''}) <ArrowRightOutlined />
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    onClick={() => navigate(activePathId ? '/courses?tab=paths' : `/courses/${courseId}`)}
                    style={{ background: '#0284c7', borderColor: '#0284c7' }}
                  >
                    {activePathId ? 'Hoàn tất lộ trình học' : 'Hoàn tất chuyên đề'} <ArrowRightOutlined />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </main>

        {/* Syllabus Sidebar - Sticky to viewport right side */}
        {!isCompact && showSidebar && (
          <aside
            style={{
              width: 340,
              flexShrink: 0,
              position: 'sticky',
              top: 56,
              height: 'calc(100vh - 56px)',
              overflowY: 'auto',
              alignSelf: 'flex-start',
              borderLeft: '1px solid #e2e8f0',
              background: '#ffffff',
              zIndex: 20,
            }}
          >
            {sidebarContent}
          </aside>
        )}
      </div>

      {/* Điện thoại / máy tính bảng: giáo trình mở dạng ngăn kéo bên phải */}
      {isCompact && (
        <Drawer
          className="lesson-syllabus-drawer"
          placement="right"
          open={syllabusDrawerOpen}
          onClose={() => setSyllabusDrawerOpen(false)}
          title={learningPath ? 'Lộ trình học' : 'Giáo trình chuyên đề'}
          size="min(360px, 88vw)"
        >
          {sidebarContent}
        </Drawer>
      )}
    </div>
  );
}
